import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  CameraIcon, 
  UserCircleIcon, 
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';

interface TeacherProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  instrumentsTaught: string[];
  specialtyInstrument: string;
  agesTaught: string[];
  specializedAgeGroup: string;
  teachingSpecialties: string[];
  specializedTeachingAreas: string[];
  languagesSpoken: string[];
  locationsOffered: string[];
  maxTravelDistance?: string;
  profilePictureUrl?: string;
}

const TeacherProfilePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get teacher data from navigation state
  const teacherData = location.state?.teacherData as TeacherProfileData;
  
  const [profilePicture, setProfilePicture] = useState<string | null>(teacherData?.profilePictureUrl || null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);

  // Log successful profile creation
  console.log('Teacher profile page loaded with data:', teacherData);

  // If no teacher data, redirect back
  if (!teacherData) {
    console.warn('No teacher data found, redirecting to signup');
    navigate('/become-a-teacher');
    return null;
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    setIsUploadingImage(true);
    console.log('Uploading profile picture:', file.name);

    try {
      // Simulate image upload - in a real app, you'd upload to a service like AWS S3
      const imageUrl = URL.createObjectURL(file);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setProfilePicture(imageUrl);
      console.log('Profile picture uploaded successfully');
      
      // TODO: Save to backend
      // await updateTeacherProfile({ profilePictureUrl: imageUrl });
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    console.log('Profile picture removed');
    // TODO: Update backend to remove profile picture
  };

  const handleDashboardNavigation = () => {
    console.log('Navigating to teacher dashboard');
    navigate('/teacher-dashboard', { state: { teacherData } });
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-8 bg-green-500/20 border border-green-500/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckIcon className="h-6 w-6 text-green-400" />
                  <div>
                    <h3 className="text-green-300 font-semibold">Profile Created Successfully!</h3>
                    <p className="text-green-200 text-sm">Your teacher profile has been created and is ready to go.</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSuccessMessage(false)}
                  className="text-green-400 hover:text-green-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Profile Header */}
          <div className="bg-slate-800 rounded-xl shadow-2xl overflow-hidden">
            
            {/* Profile Picture Section */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-8 py-12">
              <div className="flex flex-col items-center">
                <div className="relative mb-6">
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="w-32 h-32 rounded-full border-4 border-white object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="w-32 h-32 text-white" />
                  )}
                  
                  {/* Upload/Edit Button */}
                  <button
                    onClick={triggerImageUpload}
                    disabled={isUploadingImage}
                    className="absolute bottom-0 right-0 bg-slate-700 hover:bg-slate-600 text-white p-3 rounded-full shadow-lg transition-colors disabled:opacity-50"
                  >
                    {isUploadingImage ? (
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <CameraIcon className="w-5 h-5" />
                    )}
                  </button>
                  
                  {/* Remove Picture Button */}
                  {profilePicture && !isUploadingImage && (
                    <button
                      onClick={removeProfilePicture}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <h1 className="text-3xl font-bold text-white text-center">
                  {teacherData.firstName} {teacherData.lastName}
                </h1>
                <p className="text-yellow-100 text-lg mt-2">
                  {teacherData.specialtyInstrument} Teacher
                </p>
                
                {!profilePicture && (
                  <p className="text-yellow-100 text-sm mt-4 text-center">
                    Add a profile picture to help students connect with you
                  </p>
                )}
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center">
                    <PencilIcon className="w-5 h-5 mr-2 text-yellow-500" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-slate-400 text-sm">Email:</span>
                      <p className="text-slate-200">{teacherData.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-sm">Phone:</span>
                      <p className="text-slate-200">{teacherData.phoneNumber}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-sm">Languages:</span>
                      <p className="text-slate-200">{teacherData.languagesSpoken.join(', ')}</p>
                    </div>
                  </div>
                </div>

                {/* Teaching Information */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-4">Teaching Details</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-slate-400 text-sm">Instruments:</span>
                      <p className="text-slate-200">{teacherData.instrumentsTaught.join(', ')}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-sm">Age Groups:</span>
                      <p className="text-slate-200">{teacherData.agesTaught.join(', ')}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-sm">Lesson Types:</span>
                      <p className="text-slate-200">{teacherData.locationsOffered.join(', ')}</p>
                    </div>
                    {teacherData.maxTravelDistance && (
                      <div>
                        <span className="text-slate-400 text-sm">Max Travel Distance:</span>
                        <p className="text-slate-200">{teacherData.maxTravelDistance}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Specializations */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4">Specializations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="text-slate-400 text-sm">Top Specializations:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(teacherData.specializedTeachingAreas || []).map((area, index) => (
                          <span 
                            key={index}
                            className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-sm border border-yellow-500/30"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-slate-400 text-sm">All Teaching Areas:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(teacherData.teachingSpecialties || []).slice(0, 6).map((specialty, index) => (
                          <span 
                            key={index}
                            className="bg-slate-600/50 text-slate-300 px-3 py-1 rounded-full text-sm"
                          >
                            {specialty}
                          </span>
                        ))}
                        {(teacherData.teachingSpecialties || []).length > 6 && (
                          <span className="text-slate-400 text-sm px-3 py-1">
                            +{(teacherData.teachingSpecialties || []).length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-slate-600">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleDashboardNavigation}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/teacher/edit-profile')}
                    className="flex-1 border border-slate-600 text-slate-300 hover:text-slate-100 hover:border-slate-500 font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Edit Profile
                  </button>
                </div>
                
                <p className="text-slate-400 text-sm text-center mt-4">
                  Your profile is now live and students can book lessons with you!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
    </MinimalLayout>
  );
};

export default TeacherProfilePage; 